// Phase 4 — destructive restore from a GitHub backup. Fetches the
// remote data/ subtree, replaces local localStorage entries for the
// stores that have git write-through (channels, library, templates,
// default-template pref), wipes the in-browser git repo so its
// history doesn't desync from the restored content, then reloads
// the page. Zustand stores re-hydrate from the freshly-written
// localStorage on mount.
//
// Why destructive only (no merge): merge semantics are hard — what
// happens when local has channel X edited and remote has channel X
// also edited? Last-write-wins per-field would silently drop edits.
// Tree-replacement is honest: you're choosing one source of truth.
//
// The local git repo (IndexedDB store named "playground-git") gets
// deleted entirely. Next mutation lazy-inits a fresh repo and starts
// writing through to git again, which then matches the restored
// content. Auto-push catches it up to GitHub on its next debounce.

import { pullSnapshot, type BackupAuth } from "./backup-sync";

const STORAGE_CHANNELS  = "playground.channels";
const STORAGE_ACTIVE_ID = "playground.active_channel_id";
const STORAGE_LIBRARY   = "playground.library_documents";
const STORAGE_TEMPLATES = "playground.channel_templates";
const STORAGE_DEFAULT   = "playground.default_template_id";
const IDB_REPO_NAME     = "playground-git";

export interface RestoreReport {
  channels: number;
  library: number;
  templates: number;
  defaultTemplate: boolean;
  skipped: Array<{ path: string; reason: string }>;
}

/** Pull from the remote backup repo and replace local state. Returns
 *  a report of what was restored; reload happens at the call site
 *  AFTER the user sees the success state. */
export async function restoreFromBackup(auth: BackupAuth): Promise<RestoreReport> {
  const snap = await pullSnapshot(auth);
  if (snap.files.length === 0) {
    throw new Error("Remote backup is empty — nothing to restore from. Push something first.");
  }

  const channels: unknown[] = [];
  const library: unknown[] = [];
  const templates: unknown[] = [];
  let defaultId: string | null = null;
  const skipped: Array<{ path: string; reason: string }> = [];

  for (const file of snap.files) {
    try {
      const parsed = JSON.parse(file.content);
      if (file.path.startsWith("data/channels/")) {
        channels.push(parsed);
      } else if (file.path.startsWith("data/library/")) {
        library.push(parsed);
      } else if (file.path.startsWith("data/templates/")) {
        templates.push(parsed);
      } else if (file.path === "data/prefs/default-template.json") {
        const ptr = parsed as { defaultId?: string | null };
        defaultId = ptr.defaultId ?? null;
      } else {
        skipped.push({ path: file.path, reason: "unknown data subpath" });
      }
    } catch (e) {
      skipped.push({
        path: file.path,
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  }

  // Wipe-and-replace localStorage. Stores re-hydrate on the
  // subsequent page reload via their existing load() helpers.
  if (typeof localStorage !== "undefined") {
    if (channels.length > 0) {
      localStorage.setItem(STORAGE_CHANNELS, JSON.stringify(channels));
      // Active-channel id may point at a channel that doesn't exist
      // anymore; clear so the first available channel auto-selects.
      localStorage.removeItem(STORAGE_ACTIVE_ID);
    } else {
      localStorage.removeItem(STORAGE_CHANNELS);
      localStorage.removeItem(STORAGE_ACTIVE_ID);
    }
    if (library.length > 0) {
      localStorage.setItem(STORAGE_LIBRARY, JSON.stringify(library));
    } else {
      localStorage.removeItem(STORAGE_LIBRARY);
    }
    if (templates.length > 0) {
      localStorage.setItem(STORAGE_TEMPLATES, JSON.stringify(templates));
    } else {
      localStorage.removeItem(STORAGE_TEMPLATES);
    }
    if (defaultId) {
      localStorage.setItem(STORAGE_DEFAULT, defaultId);
    } else {
      localStorage.removeItem(STORAGE_DEFAULT);
    }
  }

  // Drop the in-browser git repo so its commit history doesn't
  // diverge from the restored content. The next mutation lazy-inits
  // a fresh repo and writes through normally; auto-push then catches
  // the new local commits up to the remote.
  await dropGitRepo();

  return {
    channels: channels.length,
    library: library.length,
    templates: templates.length,
    defaultTemplate: defaultId !== null,
    skipped,
  };
}

async function dropGitRepo(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(IDB_REPO_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => {
      console.warn("[restore] indexedDB.deleteDatabase failed:", req.error);
      resolve(); // non-fatal; restored data is in localStorage already
    };
    req.onblocked = () => {
      // Another tab has the DB open. We still resolve so the reload
      // happens; the new tab will see the empty IDB after the reload.
      console.warn("[restore] indexedDB.deleteDatabase blocked (other tab open)");
      resolve();
    };
  });
}
